import { useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import MedsiLogo from 'assets/medsi-block.png';
import LadyScan from 'assets/lady-scan-right.png';
import MedsiCheckFull from 'assets/medsi-check-full.png';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import useAuthStore from 'store/auth';
import { PATH_ROUTES } from 'routes/path';

const LANGUAGES = [
  { code: 'en', lang: 'English', key: 'En' },
  { code: 'es', lang: 'Spanish', key: 'Es' },
];

export default function AuthLayout() {
  const locale = useAuthStore((store) => store.locale);
  const setLocale = useAuthStore((store) => store.setLocale);

  const location = useLocation();
  const { i18n, t } = useTranslation();

  const isHsbcVerification = location.pathname === PATH_ROUTES.HSBC_VERIFICATION;

  useEffect(() => {
    document.body.dir = i18n.dir();
  }, [i18n, i18n.language]);

  const onLangChange = (lang: 'en' | 'es') => {
    setLocale(lang);
    i18n.changeLanguage(lang);
  };

  return (
    <div className='relative grid grid-cols-1 lg:grid-cols-12 font-isidora'>
      <div className='absolute z-50 hidden sm:block right-4 top-4'>
        <Select value={locale} onValueChange={onLangChange}>
          <SelectTrigger className='border-transparent max-lg:bg-primary w-fit focus:ring-transparent focus:ring-0'>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {LANGUAGES.map(({ code, lang }) => (
              <SelectItem value={code} key={code}>
                {lang}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className='flex items-center justify-center w-full h-16 sm:hidden bg-primary-dark'>
        <div className='h-6 w-fit'>
          <img
            src={MedsiCheckFull}
            alt='medsicheck'
            className='object-cover w-full h-full'
          />
        </div>

        <div className='absolute right-2'>
          <Select value={locale} onValueChange={onLangChange}>
            <SelectTrigger className='text-white bg-transparent border-transparent w-fit focus:ring-transparent focus:ring-0'>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {LANGUAGES.map(({ code, key }) => (
                <SelectItem value={code} key={code}>
                  {key}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className='relative flex justify-center w-full h-fit max-sm:pb-8 sm:h-[60vh] overflow-hidden lg:h-screen lg:col-span-7 bg-primary'>
        <div className='flex flex-col items-center justify-center px-4'>
          <div className='hidden w-40 h-40 sm:block lg:h-72 lg:w-72 aspect-square'>
            <img
              src={MedsiLogo}
              alt='medsi-logo'
              className='object-cover w-full h-full'
            />
          </div>

          <div className='max-w-xs mt-8 space-y-4 text-center text-white lg:max-w-lg'>
            <h1 className='text-xl font-semibold tracking-tight lg:text-3xl'>
              {isHsbcVerification
                ? t('hsbc_pre_approved_title')
                : t('ai_for_smarter_health_insights')}
            </h1>

            <p className='max-w-md mx-auto leading-tight lg:text-lg'>
              {isHsbcVerification
                ? t('hsbc_pre_approved_info')
                : t('health_analysis_by_ai')}
            </p>
          </div>
        </div>

        <div className='absolute bottom-0 left-0 hidden sm:block w-36 h-36 xl:w-40 xl:h-40'>
          <img
            src={LadyScan}
            alt='lady scan'
            className='object-contain h-full w-fit'
          />
        </div>
      </div>

      <div className='relative flex items-center justify-center w-full h-full mt-4 sm:mt-8 lg:mt-0 lg:col-span-5'>
        <Outlet />
      </div>
    </div>
  );
}
